import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { lookupBooking } from "@/lib/api";
import { humanSlot, paletteSummary, addonsSummary } from "@/data/collections";

const fmtDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
import { Reschedule } from "@/components/Reschedule";
import { SharePorch } from "@/components/SharePorch";
import { toast } from "sonner";

export const STATUS_META = {
  pending: { label: "With us for review", bg: "#F4E3C8", fg: "#8A5A20" },
  pending_payment: { label: "Awaiting payment", bg: "#EFE6DA", fg: "#7A6A5A" },
  approved: { label: "Reserved for you", bg: "#E1E8D8", fg: "#3F5B33" },
  denied: { label: "Denied", bg: "#EED6D2", fg: "#8A2E2E" },
};

export default function Lookup() {
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [reference, setReference] = useState(sp.get("reference") || "");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!email || !reference) return toast.error("Enter both email and reference");
    setLoading(true); setBooking(null);
    try {
      setBooking(await lookupBooking(email.trim(), reference.trim()));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No booking found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sp.get("email") && sp.get("reference")) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const palette = paletteSummary(booking?.palette);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="pp-container py-16 sm:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="pp-caption mb-3">Your reservation</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light mb-8" style={{ color: "var(--pp-burgundy)" }}>Check on your porch.</h1>
          <form onSubmit={search} className="grid grid-cols-1 sm:grid-cols-[1fr,180px,auto] gap-3 border p-3" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
            <input data-testid="lookup-email-input" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="px-4 py-3 bg-[var(--pp-cream)] outline-none text-sm" />
            <input data-testid="lookup-input" placeholder="PP-XXXXXX" value={reference} onChange={(e) => setReference(e.target.value.toUpperCase())} className="px-4 py-3 bg-[var(--pp-cream)] outline-none text-sm uppercase tracking-wider" />
            <button type="submit" data-testid="lookup-search-button" disabled={loading} className="pp-btn !py-3">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Look up
            </button>
          </form>

          {booking && (
            <article data-testid="booking-status-card" className="mt-10 border" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }}>
              <div className="p-8 flex flex-wrap items-start justify-between gap-6">
                <div>
                  <div className="pp-caption mb-1">Reference {booking.reference}</div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl" style={{ color: "var(--pp-burgundy)" }}>{booking.collection_name}</h2>
                  <p className="mt-1 text-sm" style={{ color: "var(--pp-text-muted)" }}>
                    {new Date(booking.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {humanSlot(booking.time_slot)}
                  </p>
                  {booking.previous_date && booking.status === "pending" && (
                    <p className="mt-1 text-xs italic" style={{ color: "var(--pp-brown)" }} data-testid="reschedule-note">
                      New date requested (previously {booking.previous_date} · {humanSlot(booking.previous_slot)}) — awaiting confirmation
                    </p>
                  )}
                </div>
                <span className="inline-flex items-center px-4 py-1.5 text-xs uppercase tracking-widest" style={{ backgroundColor: STATUS_META[booking.status].bg, color: STATUS_META[booking.status].fg }}>
                  {STATUS_META[booking.status].label}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-8 border-t" style={{ borderColor: "var(--pp-border)" }}>
                <Detail k="Size" v={booking.size_label} />
                <Detail k="Add-ons" v={addonsSummary(booking)} />
                {booking.customer_notes && <Detail k="Your notes" v={booking.customer_notes} />}
                {palette && <Detail k="Pumpkin palette" v={palette} />}
                {booking.priority_removal && <Detail k="Priority Removal" v={<span data-testid="lookup-priority-removal">Preferred removal{booking.removal_date ? ` on ${fmtDate(booking.removal_date)}` : ""} ($75) — guaranteed cleared by December 1</span>} />}
                <Detail k="Name" v={booking.full_name} />
                <Detail k="Address" v={booking.address} />
                <Detail k="Phone" v={booking.phone} />
                <Detail k="Email" v={booking.email} />
                {booking.is_gift && <Detail k="Gift for" v={`${booking.recipient_name}${booking.gift_message ? ` — “${booking.gift_message}”` : ""}`} />}
                {booking.pricing && (
                  <div data-testid="lookup-pricing" className="sm:col-span-2 border p-4" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream)" }}>
                    <div className="pp-caption mb-2">{booking.pricing.quote != null ? "Your quote" : "Pricing"}</div>
                    <dl className="text-sm space-y-1">
                      <div className="flex justify-between"><dt style={{ color: "var(--pp-text-muted)" }}>{booking.pricing.quote != null ? "Quoted price" : `Estimated price (${booking.size_label})`}</dt><dd>${booking.pricing.base.toFixed(2)}</dd></div>
                      {booking.pricing.discount_amount > 0 && (
                        <div className="flex justify-between" style={{ color: "#3F5B33" }}>
                          <dt>Discount ({booking.pricing.discount_type === "amount" ? `$${booking.pricing.discount_value} off` : `${booking.pricing.discount_value}% off`}{booking.discount_note ? ` — ${booking.discount_note}` : ""})</dt>
                          <dd>−${booking.pricing.discount_amount.toFixed(2)}</dd>
                        </div>
                      )}
                      <div className="flex justify-between font-medium border-t pt-1 mt-1" style={{ borderColor: "var(--pp-border)", color: "var(--pp-burgundy)" }}><dt>Your total</dt><dd data-testid="lookup-total">${booking.pricing.total.toFixed(2)}</dd></div>
                    </dl>
                    <p className="mt-2 text-xs" data-testid="lookup-deposit-status" style={{ color: booking.deposit_paid ? "#3F5B33" : "#8A5A20" }}>
                      {booking.deposit_paid ? `✓ Paid in full${booking.paid_amount ? ` ($${Number(booking.paid_amount).toLocaleString()})` : ""} — your date is booked.` : "Payment not yet received — your date isn't booked until checkout is complete."}
                    </p>
                  </div>
                )}
              </div>
              {booking.photo_base64 && (
                <div className="p-8 border-t" style={{ borderColor: "var(--pp-border)" }}>
                  <div className="pp-caption mb-2">Your porch photo</div>
                  <img src={booking.photo_base64} alt="Porch" className="max-h-72 object-cover" />
                </div>
              )}
              <Reschedule booking={booking} onUpdated={(b) => setBooking({ ...booking, ...b })} />
              {booking.status === "approved" && <SharePorch booking={booking} onUpdated={setBooking} />}
            </article>
          )}
        </div>
      </section>
      <div className="flex-1" />
      <Footer />
    </div>
  );
}

const Detail = ({ k, v }) => (
  <div>
    <div className="pp-caption mb-0.5">{k}</div>
    <div className="text-sm" style={{ color: "var(--pp-text)" }}>{v}</div>
  </div>
);
